import { useRef, useState } from "react";
import { pdfRectToViewport, viewportRectToPdf } from "./pdfCoordinates";
import { groupSospetti } from "./bboxUtils";

function normalizeDragRect(start, end) {
  const x = Math.min(start.x, end.x);
  const y = Math.min(start.y, end.y);
  return { x, y, width: Math.abs(end.x - start.x), height: Math.abs(end.y - start.y) };
}

function newBBoxId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `bbox-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// Terzo stato. Il contrassegno e' SOLO additivo: bordo ambra pieno + pallino,
// a riempimento identico a quello di un'area normale. Mai un fill piu' chiaro,
// che si leggerebbe come "meno oscurata": l'arbitro non sbianca niente.
const SOSPETTO_C = "#D97706";

/** Etichetta di una segnalazione: la categoria va SEMPRE accanto. */
export function sospettoLabel(bbox) {
  const cat = (bbox?.categoria || "ALTRO").toUpperCase();
  return `${cat} — Da controllare`;
}

/**
 * @param {{
 *   viewport: import('pdfjs-dist').PageViewport,
 *   pageNum: number,
 *   bboxes: import('./bboxUtils').PdfBBox[],
 *   selectedIds: ReadonlySet<string>,
 *   tool: 'pan' | 'bbox',
 *   onAddBbox: (bbox: import('./bboxUtils').PdfBBox) => void,
 *   onSelectBbox: (id: string | null, event?: { ctrlKey?: boolean, metaKey?: boolean, shiftKey?: boolean }) => void,
 *   confermati?: ReadonlySet<string>,
 * }} props
 */
export default function PdfOverlay({
  viewport,
  pageNum,
  bboxes,
  selectedIds,
  tool,
  onAddBbox,
  onSelectBbox,
  confermati,
}) {
  const overlayRef = useRef(null);
  const [draft, setDraft] = useState(null);
  const dragStart = useRef(null);

  const pageBboxes = bboxes.filter((b) => b.page === pageNum);
  // Chip e pallino si disegnano una volta per gruppo di parole adiacenti, non
  // per parola: due etichette larghe sulla stessa riga si copriderebbero a
  // vicenda, e una categoria coperta a meta' e' esattamente quella che
  // l'utente deve poter leggere.
  //
  // Un gruppo gia' confermato smette di essere ambra e torna un'area come le
  // altre: resta oscurato esattamente come prima - confermare non cambia il
  // documento - ma sparisce dalla coda, e la pagina mostra quanto lavoro
  // manca invece di restare punteggiata di segnalazioni gia' viste.
  const pageRuns = groupSospetti(pageBboxes).filter((r) => !confermati?.has(r.id));
  const idsAmbra = new Set(pageRuns.flatMap((r) => r.items.map((w) => w.id)));

  const localPoint = (clientX, clientY) => {
    const el = overlayRef.current;
    const rect = el.getBoundingClientRect();
    const scaleX = viewport.width / rect.width;
    const scaleY = viewport.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const handlePointerDown = (e) => {
    if (tool !== "bbox" || e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const pt = localPoint(e.clientX, e.clientY);
    dragStart.current = pt;
    setDraft({ x: pt.x, y: pt.y, width: 0, height: 0 });
    onSelectBbox(null);
  };

  const handlePointerMove = (e) => {
    if (!dragStart.current) return;
    const pt = localPoint(e.clientX, e.clientY);
    setDraft(normalizeDragRect(dragStart.current, pt));
  };

  const handlePointerUp = (e) => {
    if (!dragStart.current) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    const pt = localPoint(e.clientX, e.clientY);
    const rect = normalizeDragRect(dragStart.current, pt);
    dragStart.current = null;
    setDraft(null);

    if (rect.width < 4 || rect.height < 4) return;

    const pdfRect = viewportRectToPdf(viewport, rect);
    onAddBbox({ id: newBBoxId(), page: pageNum, ...pdfRect });
  };

  const cursor = tool === "bbox" ? "crosshair" : "default";

  return (
    <div
      ref={overlayRef}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width: viewport.width,
        height: viewport.height,
        cursor,
        // Only swallow touch gestures while drawing. In select mode, let the
        // viewer pan/scroll natively (critical on phones where the page overflows).
        touchAction: tool === "bbox" ? "none" : "auto",
      }}
      onPointerDown={(e) => {
        if (tool === "pan" && e.target === e.currentTarget) onSelectBbox(null);
        handlePointerDown(e);
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {pageBboxes.map((bbox) => {
        const rect = pdfRectToViewport(viewport, bbox);
        const selected = selectedIds.has(bbox.id);
        const sospetto = idsAmbra.has(bbox.id);
        // Il riempimento e' lo stesso di un'area normale, sospetta o no.
        const fill = selected ? "rgba(0,156,221,0.12)" : "rgba(0,156,221,0.06)";

        return (
          <div
            key={bbox.id}
            title={bbox.label || undefined}
            style={{
              position: "absolute",
              left: rect.x,
              top: rect.y,
              width: rect.width,
              height: rect.height,
              border: sospetto
                ? `2px solid ${SOSPETTO_C}`
                : selected ? "2px solid #009CDD" : "2px dashed rgba(0,156,221,0.85)",
              // Su un'area sospetta la selezione e' un anello esterno: il
              // bordo ambra deve restare riconoscibile anche da selezionata.
              boxShadow: sospetto && selected ? "0 0 0 2px #009CDD" : undefined,
              background: fill,
              boxSizing: "border-box",
              pointerEvents: tool === "pan" ? "auto" : "none",
            }}
            onPointerDown={(e) => {
              if (tool !== "pan") return;
              e.stopPropagation();
              onSelectBbox(bbox.id, e);
            }}
          />
        );
      })}

      {pageRuns.map((run) => {
        const rect = pdfRectToViewport(viewport, run);
        const label = sospettoLabel(run);
        // Il chip sta sopra il gruppo, o sotto se in cima alla pagina non ci
        // sta. Il pallino va sempre dal lato opposto, se no lo copre.
        const chipAbove = rect.y >= 20;
        // Larghezza stimata dal testo: serve solo a non far sbordare il chip
        // oltre il margine destro della pagina. Misurato a 10px/700 con
        // letter-spacing: ~6,5px per carattere. Arrotondo per eccesso, cosi'
        // l'errore fa rientrare il chip invece di farlo uscire.
        const chipW = label.length * 6.8 + 12;
        const left = Math.min(
          Math.max(0, rect.x),
          Math.max(0, viewport.width - chipW - 2),
        );

        return (
          <div key={`sosp-${run.id}`} style={{ pointerEvents: "none" }}>
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                left: rect.x + rect.width - 4,
                top: chipAbove ? rect.y + rect.height - 4 : rect.y - 5,
                width: 9,
                height: 9,
                borderRadius: "50%",
                background: SOSPETTO_C,
                border: "1.5px solid #fff",
                boxSizing: "border-box",
              }}
            />
            <span
              style={{
                position: "absolute",
                left,
                top: chipAbove ? rect.y - 18 : rect.y + rect.height + 4,
                padding: "1px 6px",
                borderRadius: 4,
                background: SOSPETTO_C,
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                lineHeight: "16px",
                letterSpacing: "0.02em",
                whiteSpace: "nowrap",
                boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
              }}
            >
              {label}
            </span>
          </div>
        );
      })}

      {draft && (
        <div
          style={{
            position: "absolute",
            left: draft.x,
            top: draft.y,
            width: draft.width,
            height: draft.height,
            border: "2px dashed #59C9F4",
            background: "rgba(89,201,244,0.1)",
            boxSizing: "border-box",
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );
}
